import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../../config/apiConfig';
import {
  isValidFlashlightColorKey,
  normalizeFlashlightColorForDisplay,
} from '../../../constants/flashlightColors';
import { playSoundEffect } from '../../../hooks/useSoundEffects';
import ExploreUnknownMinigameFlashlightDarkness from '../../ExploreUnknownMinigameFlashlightDarkness';
import {
  WOMBAFLEKS_CATCH_BACKGROUND_SRC,
  WOMBAFLEKS_CATCH_SPRITE_SRC,
  WOMBAFLEKS_CATCH_POSITION_INTERVAL_MS,
  WOMBAFLEKS_CATCH_TIMER_DURATION_MS,
  WOMBAFLEKS_CATCH_WIN_PICK_VALUE,
  WOMBAFLEKS_CATCH_LOSS_PICK_VALUE,
  WOMBAFLEKS_CATCH_SUCCESS_SOUND_SRC,
  pickRandomWombafleksLogicalPosition,
  pickRandomWombafleksRepositionSoundSrc,
  wombafleksLogicalToPercentPosition,
  formatWombafleksTimerLabel,
} from './wombafleksCatchLayout';
import './WombafleksCatchMinigame.css';

/**
 * @param {object} props
 * @param {{ winPickValue: number }} props.challenge
 * @param {(pickedTreeIndex: number) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 */
export default function WombafleksCatchMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
}) {
  const { t } = useTranslation();
  const playArenaRef = useRef(null);
  const pickedRef = useRef(false);
  const targetPointerDownRef = useRef(false);
  const timersRef = useRef([]);

  const [spawnPos, setSpawnPos] = useState(() => pickRandomWombafleksLogicalPosition());
  const [msRemaining, setMsRemaining] = useState(WOMBAFLEKS_CATCH_TIMER_DURATION_MS);
  const [flashlightColorKey, setFlashlightColorKey] = useState('white');

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.wombafleks.winReward.${winReward.id}`
      : null;

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    fetch(`${API_URL}/profile/flashlight-color`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`flashlight-color ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled || data == null || typeof data.color !== 'string') {
          throw new Error('Invalid flashlight-color response');
        }
        if (!isValidFlashlightColorKey(data.color)) {
          throw new Error(`Invalid flashlight color key: ${data.color}`);
        }
        setFlashlightColorKey(normalizeFlashlightColorForDisplay(data.color));
      })
      .catch((err) => {
        console.error('WombafleksCatchMinigame: failed to load flashlight color', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) {
      window.clearTimeout(id);
      window.clearInterval(id);
    }
    timersRef.current = [];
  }, []);

  const repositionWombafleks = useCallback(() => {
    setSpawnPos(pickRandomWombafleksLogicalPosition());
    playSoundEffect(pickRandomWombafleksRepositionSoundSrc());
  }, []);

  const submitPick = useCallback(
    (pickValue) => {
      if (pickedRef.current || disabled || resultOutcome != null) {
        return;
      }
      pickedRef.current = true;
      clearTimers();
      onPickTree(pickValue);
    },
    [clearTimers, disabled, onPickTree, resultOutcome]
  );

  useEffect(() => {
    if (resultOutcome != null) {
      clearTimers();
      return undefined;
    }
    if (!challenge || !Number.isInteger(challenge.winPickValue)) {
      throw new Error('WombafleksCatchMinigame: challenge.winPickValue is required');
    }
    if (challenge.winPickValue !== WOMBAFLEKS_CATCH_WIN_PICK_VALUE) {
      throw new Error(
        `WombafleksCatchMinigame: unexpected winPickValue (${String(challenge.winPickValue)})`
      );
    }

    pickedRef.current = false;
    clearTimers();
    repositionWombafleks();
    setMsRemaining(WOMBAFLEKS_CATCH_TIMER_DURATION_MS);

    const positionInterval = window.setInterval(() => {
      repositionWombafleks();
    }, WOMBAFLEKS_CATCH_POSITION_INTERVAL_MS);
    timersRef.current.push(positionInterval);

    const timerEnd = Date.now() + WOMBAFLEKS_CATCH_TIMER_DURATION_MS;
    const tickInterval = window.setInterval(() => {
      const left = timerEnd - Date.now();
      if (left <= 0) {
        setMsRemaining(0);
        submitPick(WOMBAFLEKS_CATCH_LOSS_PICK_VALUE);
        return;
      }
      setMsRemaining(left);
    }, 200);
    timersRef.current.push(tickInterval);

    const timeoutId = window.setTimeout(() => {
      submitPick(WOMBAFLEKS_CATCH_LOSS_PICK_VALUE);
    }, WOMBAFLEKS_CATCH_TIMER_DURATION_MS);
    timersRef.current.push(timeoutId);

    return () => {
      clearTimers();
    };
  }, [challenge, clearTimers, repositionWombafleks, resultOutcome, submitPick]);

  const handleTargetPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    targetPointerDownRef.current = true;
  }, []);

  const handleTargetPointerUp = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      if (!targetPointerDownRef.current) return;
      targetPointerDownRef.current = false;
      playSoundEffect(WOMBAFLEKS_CATCH_SUCCESS_SOUND_SRC);
      submitPick(WOMBAFLEKS_CATCH_WIN_PICK_VALUE);
    },
    [submitPick]
  );

  const handleTargetPointerCancel = useCallback(() => {
    targetPointerDownRef.current = false;
  }, []);

  const [logicalX, logicalY] = spawnPos;
  const posStyle = wombafleksLogicalToPercentPosition(logicalX, logicalY);
  const playing = resultOutcome == null && !disabled && !pickedRef.current;
  const showDarkness = playing;

  return (
    <div className="wombafleks-catch">
      <div
        ref={setPlayArenaRef}
        className="wombafleks-catch-play-arena"
        aria-label={t('exploreUnknownPage.minigame.wombafleks.playArenaAria')}
      >
        <img
          src={WOMBAFLEKS_CATCH_BACKGROUND_SRC}
          alt=""
          className="wombafleks-catch-bg"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />
        {playing ? (
          <button
            type="button"
            className="wombafleks-catch-target"
            style={posStyle}
            disabled={disabled}
            onPointerDown={handleTargetPointerDown}
            onPointerUp={handleTargetPointerUp}
            onPointerCancel={handleTargetPointerCancel}
            onDragStart={(e) => e.preventDefault()}
            aria-label={t('exploreUnknownPage.minigame.wombafleks.targetAria')}
          >
            <span
              className="wombafleks-catch-target-sprite"
              style={{ backgroundImage: `url(${WOMBAFLEKS_CATCH_SPRITE_SRC})` }}
              aria-hidden="true"
            />
          </button>
        ) : null}
        {showDarkness ? (
          <ExploreUnknownMinigameFlashlightDarkness
            playArenaRef={playArenaRef}
            flashlightColorKey={flashlightColorKey}
          />
        ) : null}

        <div className="wombafleks-catch-hud wombafleks-catch-hud--top" data-no-minigame-flashlight="true">
          <h3 id="explore-unknown-minigame-title" className="wombafleks-catch-game-title">
            {t('exploreUnknownPage.minigame.wombafleks.title')}
          </h3>
          {resultOutcome == null ? (
            <div className="wombafleks-catch-instruction" role="status">
              <span
                className="wombafleks-catch-instruction-icon"
                style={{ backgroundImage: `url(${WOMBAFLEKS_CATCH_SPRITE_SRC})` }}
                aria-hidden="true"
              />
              <p className="wombafleks-catch-instruction-text">
                {t('exploreUnknownPage.minigame.wombafleks.catchPrompt')}
              </p>
              <p className="wombafleks-catch-instruction-timer" aria-live="polite">
                {formatWombafleksTimerLabel(msRemaining)}
              </p>
            </div>
          ) : null}
        </div>

        {resultOutcome != null ? (
          <div
            className={`wombafleks-catch-hud wombafleks-catch-hud--bottom wombafleks-catch-result-banner wombafleks-catch-result-banner--${resultOutcome}`}
            role="status"
            aria-live="polite"
            data-no-minigame-flashlight="true"
          >
            <p className="wombafleks-catch-result-banner-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.wombafleks.successTitle')
                : t('exploreUnknownPage.minigame.wombafleks.failureTitle')}
            </p>
            {resultOutcome === 'won' ? (
              <p className="wombafleks-catch-result-banner-body">
                {winRewardBodyKey
                  ? t(winRewardBodyKey, {
                      percent: winReward.darknessReductionApplied,
                      xp: winReward.xpGained,
                      billyBucks: winReward.billyBucksAmount,
                      health: winReward.startHealthBonus != null ? winReward.startHealthBonus : 3,
                    })
                  : t('exploreUnknownPage.minigame.wombafleks.successBodyGeneric')}
              </p>
            ) : null}
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className={
                  resultOutcome === 'lost'
                    ? 'wombafleks-catch-result-proceed wombafleks-catch-result-proceed--lost'
                    : 'wombafleks-catch-result-proceed'
                }
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
